import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import api from '../../services/api';

const SEVERITIES = [
    { value: 'low', label: 'Baixa' },
    { value: 'medium', label: 'Média' },
    { value: 'high', label: 'Alta' },
    { value: 'critical', label: 'Crítica' }
];
const PRIORITY_TO_SEVERITY = { low: 'low', normal: 'medium', high: 'high', urgent: 'critical' };

const newRequestId = () =>
    window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
          });

// Integração com o SGQ (Sistema de Gestão da Qualidade). Só aparece quando a
// conta tem a integração ligada (`v1/sgq/status/`); com ela desligada o
// componente não renderiza nada e não faz nenhuma outra chamada.
const SgqSection = ({ appId, ticket, onOpened }) => {
    const [flags, setFlags] = useState(null);
    const [ncs, setNcs] = useState([]);
    const [open, setOpen] = useState(false);
    const [requestId, setRequestId] = useState('');
    const [form, setForm] = useState({ title: '', description: '', severity: 'medium', lot: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const loadNcs = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/tickets/${ticket.id}/nonconformities/`);
            setNcs(response.data.results || []);
        } catch (e) {
            console.error('Erro ao carregar NCs do SGQ', e);
        }
    };

    useEffect(() => {
        let cancelled = false;
        api.get('v1/sgq/status/')
            .then((response) => {
                if (cancelled) return;
                setFlags(response.data);
                if (response.data.enabled) loadNcs();
            })
            .catch(() => !cancelled && setFlags({ enabled: false }));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, ticket.id]);

    if (!flags?.enabled) return null;

    // Abre a NC no SGQ já autenticado (SSO: token curto de uso único emitido pelo CRM).
    // A aba é aberta de forma síncrona no clique para não ser barrada pelo bloqueador de pop-ups.
    const openInSgq = async (nc) => {
        const tab = window.open('', '_blank');
        try {
            const response = await api.get('v1/sgq/sso-link/', { params: { nc: nc.nc_id } });
            if (tab) tab.location.href = response.data.url;
            else window.location.assign(response.data.url);
        } catch (e) {
            if (tab) tab.close();
            setError('Não foi possível abrir o SGQ.');
        }
    };

    const openForm = () => {
        setRequestId(newRequestId()); // mesma chave se o envio for repetido: não duplica a NC
        setForm({
            title: ticket.title || '',
            description: ticket.description || '',
            severity: PRIORITY_TO_SEVERITY[ticket.priority] || 'medium',
            lot: ''
        });
        setError('');
        setOpen(true);
    };

    const submit = async () => {
        setSubmitting(true);
        setError('');
        try {
            const lot = form.lot.trim();
            await api.post(`v1/apps/${appId}/tickets/${ticket.id}/open-nc/`, {
                request_id: requestId,
                title: form.title,
                description: form.description,
                severity: form.severity,
                links: lot ? [{ system: 'erp', link_type: 'erp_lot', external_id: lot, code: lot }] : []
            });
            setOpen(false);
            await loadNcs();
            if (onOpened) onOpened();
        } catch (e) {
            setError(e.response?.data?.error || 'Não foi possível abrir a não conformidade.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
                Não conformidades (SGQ)
            </Typography>
            {error && !open && <Alert severity="error">{error}</Alert>}
            {ncs.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    Nenhuma não conformidade aberta para este ticket.
                </Typography>
            )}
            {ncs.map((nc) => (
                <Box key={nc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>
                        {nc.number}
                    </Typography>
                    <Chip size="small" label={nc.status_label || nc.status || '—'} />
                    <Button size="small" onClick={() => openInSgq(nc)}>
                        Abrir no SGQ
                    </Button>
                </Box>
            ))}
            {flags.can_open_nc && (
                <Box>
                    <Button size="small" variant="outlined" onClick={openForm}>
                        Abrir não conformidade
                    </Button>
                </Box>
            )}

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Abrir não conformidade</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="Título"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        fullWidth
                        required
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        label="Descrição"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                    <TextField
                        select
                        label="Severidade"
                        value={form.severity}
                        onChange={(e) => setForm({ ...form, severity: e.target.value })}
                        fullWidth
                    >
                        {SEVERITIES.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Lote (código no ERP)"
                        value={form.lot}
                        onChange={(e) => setForm({ ...form, lot: e.target.value })}
                        fullWidth
                        helperText="Opcional. O vínculo com o lote do ERP é guardado na NC."
                    />
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        O cliente e o link deste ticket seguem junto com a NC.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={submit} disabled={submitting || !form.title.trim()}>
                        {submitting ? <CircularProgress size={18} /> : 'Abrir NC'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SgqSection;
